// Enhanced cart-drawer.js with fixed duplication issue
class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  isUpdating = false;
  
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname.includes("cart");
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) return;
    this.init();
    this.setupAjaxCartHandlers();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });
  }

  // New method to handle AJAX add to cart from upsell items
  setupAjaxCartHandlers() {
    // Use event delegation for dynamically added buttons
    this.addEventListener("click", (e) => {
      const addButton = e.target.closest(".ajax-add-to-cart");
      if (addButton && !this.isUpdating) {
        e.preventDefault();
        e.stopPropagation();
        this.handleAjaxAddToCart(addButton);
      }
    });
  }

  async handleAjaxAddToCart(button) {
    if (button.disabled || this.isUpdating) return;

    this.isUpdating = true;
    const variantId = button.getAttribute("data-variant-id");
    const productHandle = button.getAttribute("data-product-handle");

    // Show loading state
    button.disabled = true;
    const originalContent = button.innerHTML;
    button.style.opacity = "0.5";

    try {
      // Get product data
      const productResponse = await fetch(`/products/${productHandle}.js`);
      const productData = await productResponse.json();

      // Use the specified variant or first available one
      const selectedVariant =
        productData.variants.find((v) => v.id == variantId && v.available) ||
        productData.variants.find((v) => v.available) ||
        productData.variants[0];

      // Add to cart
      const addResponse = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: selectedVariant.id,
              quantity: 1,
            },
          ],
        }),
      });

      if (!addResponse.ok) {
        throw new Error("Failed to add item to cart");
      }

      const addedItems = await addResponse.json();

      // Update cart drawer content via sections API
      await this.refreshCartDrawer();

      // Show success feedback
      this.showSuccessFeedback();

      // Open drawer if closed
      if (!this.isOpen) {
        this.toggleDrawerClasses();
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      // Show error feedback
      this.showErrorFeedback();
    } finally {
      // Re-enable button
      setTimeout(() => {
        button.disabled = false;
        button.style.opacity = "1";
        button.innerHTML = originalContent;
        this.isUpdating = false;
      }, 1000);
    }
  }

  async refreshCartDrawer() {
    try {
      // Fetch the cart drawer section
      const response = await fetch(
        `${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`
      );
      const sections = await response.json();

      if (sections["cart-drawer"]) {
        // Parse the HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections["cart-drawer"], "text/html");
        
        // Store current scroll position
        const currentDrawerBody = document.querySelector(".wt-cart__drawer__body");
        const scrollPos = currentDrawerBody ? currentDrawerBody.scrollTop : 0;
        
        // Update only the specific parts that need updating
        
        // 1. Update the header title/count (only the one in drawer header)
        const newTitleQty = doc.querySelector("#CartDrawer-TotalQty");
        const currentTitleQty = document.querySelector("#CartDrawer-TotalQty");
        if (newTitleQty && currentTitleQty) {
          currentTitleQty.innerHTML = newTitleQty.innerHTML;
        }
        
        // 2. Update the main free shipping bar (only the one directly in drawer body, before cart-drawer-items)
        const newShippingBar = doc.querySelector(".wt-cart__drawer__body > .wt-free-shipping-bar");
        const currentShippingBar = document.querySelector(".wt-cart__drawer__body > .wt-free-shipping-bar");
        if (newShippingBar && currentShippingBar) {
          currentShippingBar.outerHTML = newShippingBar.outerHTML;
        }
        
        // 3. Update the entire cart-drawer-items section but preserve the structure
        const newCartDrawerItems = doc.querySelector("cart-drawer-items");
        const currentCartDrawerItems = document.querySelector("cart-drawer-items");
        if (newCartDrawerItems && currentCartDrawerItems) {
          // Only update the inner content of cart-drawer-items, not the entire element
          // This preserves the custom element and its attributes
          currentCartDrawerItems.innerHTML = newCartDrawerItems.innerHTML;
        }
        
        // 4. Update the cross-sell section
        const newCrossSell = doc.querySelector(".wt-cart__cross-sell");
        const currentCrossSell = document.querySelector(".wt-cart__cross-sell");
        if (newCrossSell && currentCrossSell) {
          currentCrossSell.outerHTML = newCrossSell.outerHTML;
        }
        
        // 5. Update the footer
        const newFooter = doc.querySelector(".wt-cart__drawer__footer");
        const currentFooter = document.querySelector(".wt-cart__drawer__footer");
        if (newFooter && currentFooter) {
          currentFooter.innerHTML = newFooter.innerHTML;
        }
        
        // Restore scroll position
        const newDrawerBody = document.querySelector(".wt-cart__drawer__body");
        if (newDrawerBody) {
          newDrawerBody.scrollTop = scrollPos;
        }
      }

      // Update cart icon bubble
      if (sections["cart-icon-bubble"]) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections["cart-icon-bubble"], "text/html");
        const newBubble = doc.querySelector(".shopify-section");
        const bubbleElement = document.getElementById("cart-icon-bubble");
        
        if (bubbleElement && newBubble) {
          bubbleElement.innerHTML = newBubble.innerHTML;
        }
      }

      // Get updated cart data for count
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();
      this.updateCartCount(cart.item_count);

      // Trigger custom event for other components
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart },
        })
      );

      // Trigger Shopify's pubsub if available
      if (typeof publish !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "ajax-add" });
      }
    } catch (error) {
      console.error("Error refreshing cart drawer:", error);
      // Fallback: reload the page if update fails
      window.location.reload();
    }
  }

  updateCartCount(count) {
    // Update all cart count elements
    const cartCounts = document.querySelectorAll(
      ".cart-count-bubble, [data-cart-count]"
    );
    cartCounts.forEach((element) => {
      element.textContent = count;
      if (count > 0) {
        element.style.display = "";
      }
    });
  }

  showSuccessFeedback() {
    // Check if notification already exists
    let notification = document.querySelector(".cart-add-notification");
    
    if (!notification) {
      notification = document.createElement("div");
      notification.className = "cart-add-notification";
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #000;
        color: #fff;
        padding: 12px 20px;
        border-radius: 4px;
        z-index: 10000;
        font-size: 14px;
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;
      `;
      document.body.appendChild(notification);
    }

    notification.textContent = "✓ Item added to cart!";
    notification.style.opacity = "1";

    setTimeout(() => {
      notification.style.opacity = "0";
    }, 2000);
  }

  showErrorFeedback() {
    let notification = document.querySelector(".cart-add-notification");
    
    if (!notification) {
      notification = document.createElement("div");
      notification.className = "cart-add-notification";
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #d32f2f;
        color: #fff;
        padding: 12px 20px;
        border-radius: 4px;
        z-index: 10000;
        font-size: 14px;
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;
      `;
      document.body.appendChild(notification);
    }

    notification.textContent = "✗ Could not add item to cart";
    notification.style.opacity = "1";

    setTimeout(() => {
      notification.style.opacity = "0";
    }, 2000);
  }

  renderContents(parsedState, isClosedCart = true) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      }
    });

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// Helper function for setting tabindex
function setTabindex(elements, value) {
  if (elements && elements.length > 0) {
    elements.forEach(element => {
      element.setAttribute('tabindex', value);
    });
  }
}

// Fallback for PUB_SUB_EVENTS if not defined
if (typeof PUB_SUB_EVENTS === 'undefined') {
  window.PUB_SUB_EVENTS = {
    cartUpdate: 'cart-update'
  };
}

// Fallback for subscribe function if not defined
if (typeof subscribe === 'undefined') {
  window.subscribe = (event, callback) => {
    console.log('Subscribe function not available, using fallback');
    return () => {}; // Return empty unsubscribe function
  };
}

// Fallback for publish function if not defined  
if (typeof publish === 'undefined') {
  window.publish = (event, data) => {
    console.log('Publish function not available, using fallback');
    document.dispatchEvent(new CustomEvent(event, { detail: data }));
  };
}